<script lang="ts">
    import { t } from '$lib/i18n';
    import { formatViewCount } from '$lib/utils/numberUtils';
    import { goto } from '$app/navigation';
    import { flip } from 'svelte/animate';
    import { quintOut } from 'svelte/easing';
    import { ChevronDown, ChevronUp } from 'lucide-svelte';

    export let isRankingVisible: boolean;
    export let liveRankingStore: any;
    export let selectedGroup: string;
    export let followedLiveStreams: any[];
    export let groups: any[] | null;
    export let filteredLiveRankings: any[];
    export let videoDetails: any;
    export let data: any;
    export let bindSelectedVideoButton: (node: HTMLElement, isSelected: boolean) => { destroy(): void };
    
    function toggleRanking() {
        isRankingVisible = !isRankingVisible;
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('rankingToggle', { detail: { isVisible: isRankingVisible } }));
        }
    }
    
    function handleRelatedVideoClick(videoId: string) {
        goto(`/video/${videoId}`);
    }
</script>

{#if $liveRankingStore.length > 0}
<div class="lg:hidden fixed bottom-0 left-0 right-0 {!isRankingVisible ? 'pointer-events-none' : ''}">
    <!-- 常に表示されるボタン -->
    <button
        on:click={toggleRanking}
        class="absolute {isRankingVisible ? 'top-2 left-4' : 'bottom-4 left-4'} p-1 bg-white/90 dark:bg-neutral-800/90 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-full transition-colors backdrop-blur-md z-50 shadow-md border border-gray-200 dark:border-neutral-700 pointer-events-auto"
        aria-label={isRankingVisible ? 'ランキングを隠す' : 'ランキングを表示'}
    >
        {#if isRankingVisible}
            <ChevronDown class="w-4 h-4 text-gray-700 dark:text-white" />
        {:else}
            <ChevronUp class="w-4 h-4 text-gray-700 dark:text-white" />
        {/if}
    </button>

    <!-- ランキング本体 -->
    <div class="transition-transform duration-300 ease-in-out {isRankingVisible ? 'translate-y-0 pointer-events-auto' : 'translate-y-full pointer-events-none'} backdrop-blur-md border-t border-neutral-200 dark:border-neutral-800 bg-white/90 dark:bg-neutral-900/90 pb-[env(safe-area-inset-bottom)]">
        <div class="flex items-center justify-between px-4 pt-2 pb-1">
            <div class="flex items-center gap-2">
                <div class="w-6"></div>
                <h2 class="text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t('video_page.live_now', '配信中')}
                </h2>
                <div class="relative flex items-center">
                    <select
                        bind:value={selectedGroup}
                        class="appearance-none bg-gray-100 dark:bg-neutral-900/50 border border-gray-300 dark:border-neutral-700 text-gray-800 dark:text-neutral-400 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent block w-32 py-1 pl-2 pr-6 transition-colors"
                    >
                        <option value="" class="bg-white dark:bg-neutral-900">全て ({$liveRankingStore.length})</option>
                        <option value="followed" class="bg-white dark:bg-neutral-900">フォロー中 ({followedLiveStreams.length})</option>
                        {#each groups || [] as group}
                            {@const count = $liveRankingStore.filter(video => 
                                data.groupChannels?.some(channel => 
                                    channel.id === video.channel_id && channel.group_id === group.id
                                ) || false
                            ).length}
                            <option value={group.id} class="bg-white dark:bg-neutral-900">{group.name} ({count})</option>
                        {/each}
                    </select>
                    <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1 text-gray-500 dark:text-neutral-500">
                        <svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                        </svg>
                    </div>
                </div>
            </div>
            <a 
                href="/live-rankings"
                class="flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-neutral-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
                {t('video_page.live_rankings', 'Rankings')}
                <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M5 12h14m-7-7 7 7-7 7" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </a>
        </div>

        <!-- カルーセル -->
        <div class="overflow-x-auto scrollbar-hide pb-2">
            <div class="flex gap-3 px-4 py-2 min-w-full">
                {#if selectedGroup === 'followed'}
                    {#if followedLiveStreams.length === 0}
                        <div class="text-gray-800 dark:text-white text-sm px-2">
                            フォロー中のチャンネルの配信はありません。
                        </div>
                    {:else}
                        {#each followedLiveStreams as video, i (video.id)}
                            <button 
                                animate:flip={{duration: 500, easing: quintOut}}
                                on:click={() => handleRelatedVideoClick(video.id)}
                                on:keydown={(e) => e.key === 'Enter' && handleRelatedVideoClick(video.id)}
                                class="flex-shrink-0 w-32 group text-left transition-all duration-200 {video.id === videoDetails.id ? 'scale-105 shadow-lg shadow-pink-500/20' : ''}"
                                data-video-id={video.id}
                                use:bindSelectedVideoButton={video.id === videoDetails.id}
                            >
                                {#if video.id === videoDetails.id}
                                    <div class="absolute inset-0 ring-2 ring-pink-500 rounded-lg z-10"></div>
                                {/if}
                                <div class="relative w-full pb-[56.25%] bg-neutral-950 rounded-lg overflow-hidden">
                                    <img 
                                        src={video.thumbnail_url} 
                                        alt={video.title}
                                        class="absolute top-0 left-0 w-full h-full object-cover"
                                    />
                                    <div class="absolute top-0 left-0 px-1 py-0.5 bg-red-500 rounded text-[10px] text-white font-medium flex items-center gap-0.5">
                                        <span class="w-1 h-1 rounded-full bg-white animate-pulse"></span>
                                        LIVE
                                    </div>
                                    <div class="absolute bottom-0 right-0 px-1 py-0.5 bg-black/70 rounded text-[10px] text-white font-medium">
                                        <div class="flex items-center gap-1">
                                            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z" />
                                            </svg>
                                            {formatViewCount(video.currentViewers)}
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-1.5 text-xs">
                                    <p class="font-medium line-clamp-1 text-gray-900 dark:text-neutral-100 text-[10px]">
                                        {video.title}
                                    </p>
                                    <p class="text-gray-500 dark:text-neutral-400 line-clamp-1 mt-0.5 text-[9px]">
                                        {video.channel_title}
                                    </p>
                                </div>
                            </button>
                        {/each}
                    {/if}
                {:else}
                    {#if filteredLiveRankings.length === 0}
                        <div class="text-gray-800 dark:text-white text-sm px-2">
                            選択したグループに属するチャンネルの配信はありません。
                        </div>
                    {:else}
                        {#each filteredLiveRankings as video, i (video.id)}
                            <button 
                                animate:flip={{duration: 500, easing: quintOut}}
                                on:click={() => handleRelatedVideoClick(video.id)}
                                on:keydown={(e) => e.key === 'Enter' && handleRelatedVideoClick(video.id)}
                                class="flex-shrink-0 w-32 group text-left transition-all duration-200 {video.id === videoDetails.id ? 'scale-105 shadow-lg shadow-pink-500/20' : ''}"
                                data-video-id={video.id}
                                use:bindSelectedVideoButton={video.id === videoDetails.id}
                            >
                                {#if video.id === videoDetails.id}
                                    <div class="absolute inset-0 ring-2 ring-pink-500 rounded-lg z-10"></div>
                                {/if}
                                <div class="relative w-full pb-[56.25%] bg-neutral-950 rounded-lg overflow-hidden">
                                    <img 
                                        src={video.thumbnail_url} 
                                        alt={video.title}
                                        class="absolute top-0 left-0 w-full h-full object-cover"
                                    />
                                    <div class="absolute top-0 left-0 px-1 py-0.5 bg-red-500 rounded text-[10px] text-white font-medium flex items-center gap-0.5">
                                        <span class="w-1 h-1 rounded-full bg-white animate-pulse"></span>
                                        LIVE
                                    </div>
                                    <div class="absolute bottom-0 right-0 px-1 py-0.5 bg-black/70 rounded text-[10px] text-white font-medium">
                                        <div class="flex items-center gap-1">
                                            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z" />
                                            </svg>
                                            {formatViewCount(video.currentViewers)}
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-1.5 text-xs">
                                    <p class="font-medium line-clamp-1 text-gray-900 dark:text-neutral-100 text-[10px]">
                                        {video.title}
                                    </p>
                                    <p class="text-gray-500 dark:text-neutral-400 line-clamp-1 mt-0.5 text-[9px]">
                                        {video.channel_title}
                                    </p>
                                </div>
                            </button>
                        {/each}
                    {/if}
                {/if}
            </div>
        </div>
    </div>
</div>
{/if} 